import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const ENTRY_KEY = "arias_entry_attestation_v1";

export default function EntryGate() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [researcher, setResearcher] = useState(false);
  const [ruo, setRuo] = useState(false);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    const skip = location.pathname === "/admin" || location.hash?.includes("session_id=");
    if (!skip && !localStorage.getItem(ENTRY_KEY)) setOpen(true);
  }, [location.pathname, location.hash]);

  const enter = () => {
    localStorage.setItem(ENTRY_KEY, new Date().toISOString());
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-lg rounded-sm [&>button]:hidden" data-testid="entry-gate-modal"
        onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <BrandMark className="h-8 w-8" />
            <span className="font-display font-bold text-slate-900">Arias <span className="text-sky-700">Peptides</span></span>
            <span className="ml-auto font-tech text-[10px] uppercase tracking-[0.16em] text-slate-400">Research-use attestation</span>
          </div>
          <p className="font-tech text-[11px] uppercase tracking-[0.16em] text-amber-700">A brief confirmation</p>
          <DialogTitle className="font-display text-2xl">You're entering a research supply catalog.</DialogTitle>
          <DialogDescription className="text-sm text-slate-600 leading-relaxed">
            Arias Peptides distributes research compounds and related lab supplies for laboratory research use only.
            These products are not drugs, not dietary supplements, and are not intended for human or animal consumption.
            To continue, please confirm:
          </DialogDescription>
        </DialogHeader>
        {declined ? (
          <div className="border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-3" data-testid="entry-gate-declined">
            <p>Thanks for your honesty. This catalog is available only to qualified researchers acting in a research capacity.</p>
            <Button variant="outline" onClick={() => setDeclined(false)} data-testid="entry-gate-reconsider-btn">Go back</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox id="gate-researcher" checked={researcher} onCheckedChange={(v) => setResearcher(!!v)} data-testid="entry-gate-researcher-checkbox" className="mt-0.5" />
              <Label htmlFor="gate-researcher" className="text-sm text-slate-800 leading-snug cursor-pointer">
                I am a qualified researcher, 21 years or older, acting in a research or institutional purchasing capacity.
              </Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="gate-ruo" checked={ruo} onCheckedChange={(v) => setRuo(!!v)} data-testid="entry-gate-ruo-checkbox" className="mt-0.5" />
              <Label htmlFor="gate-ruo" className="text-sm text-slate-800 leading-snug cursor-pointer">
                I understand these products are for <em>in vitro</em> research only and will not be used in humans, animals, food, or as a drug.
              </Label>
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-200">
              <button onClick={() => setDeclined(true)} data-testid="entry-gate-decline-btn" className="text-xs text-slate-500 hover:text-slate-800 transition-colors">
                I don't meet these criteria
              </button>
              <Button disabled={!researcher || !ruo} onClick={enter} data-testid="entry-gate-enter-btn" className="gap-2">
                <ShieldCheck className="h-4 w-4" /> Enter the catalog
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
